import { PagedResultDto } from '@abp/ng.core';
import { waitForProjectionCondition, ProjectionPollingOptions, MAX_PAGE_SIZE } from '@zendra/shared';
import { concatMap, from, map, Observable, of, toArray } from 'rxjs';
import { LidarProjectionChangesDto } from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/lidar-inventory/models';
import {
  GetLidarInventoryDataEntryProjectionListInputDto,
  LidarInventoryDataEntryProjectionDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/lidar-inventory-data-entries/models';

export function waitForLidarProjectionChanges(
  changes: LidarProjectionChangesDto,
  load: (input: GetLidarInventoryDataEntryProjectionListInputDto) => Observable<PagedResultDto<LidarInventoryDataEntryProjectionDto>>,
  polling?: ProjectionPollingOptions,
): Observable<boolean> {
  const upserts = changes.upserts ?? [];
  const deletedIds = changes.deletedIds ?? [];
  const ids = [...new Set([...upserts.map(item => item.id), ...deletedIds])];
  if (ids.length === 0) return of(true);
  // An incomplete command result must not accidentally confirm an unrelated row.
  if (ids.some(id => typeof id !== 'number') || upserts.some(item => !item.concurrencyStamp)) {
    return of(false);
  }
  const chunks: number[][] = [];
  for (let index = 0; index < ids.length; index += MAX_PAGE_SIZE) {
    chunks.push(ids.slice(index, index + MAX_PAGE_SIZE) as number[]);
  }

  return waitForProjectionCondition(() => from(chunks).pipe(
    concatMap(chunk => load({
      lidarInventoryDataEntryId: chunk,
      skipCount: 0,
      maxResultCount: MAX_PAGE_SIZE,
    })),
    toArray(),
    map(pages => {
      const rows = pages.flatMap(page => page.items ?? []);
      const byId = new Map(rows.map(row => [row.lidarInventoryDataEntryId, row]));
      return upserts.every(item => byId.get(item.id)?.concurrencyStamp === item.concurrencyStamp)
        && deletedIds.every(id => !byId.has(id));
    }),
  ), polling);
}
